import React, { useState } from "react";
import { FaCog, FaTimes, FaUser, FaFileAlt, FaComment, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaThumbsUp, FaThumbsDown } from "react-icons/fa";
import api from "../utils/api";
import { formatRequestTimestamp } from "../utils/formatDateTime";

export default function ProcessRequestModal({ isOpen, onClose, request, onRequestProcessed }) {
  const [status, setStatus] = useState("approved");
  const [adminComments, setAdminComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await api.put(`/request/${request._id}/process`, {
        status,
        adminComments
      });

      onRequestProcessed();
      onClose();
      setStatus("approved");
      setAdminComments("");
    } catch (error) {
      setError(error.response?.data?.error || "Failed to process request");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !request) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="relative bg-gray-900 p-8 rounded-2xl shadow-2xl w-full max-w-md mx-2 animate-fadeIn">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white text-2xl font-bold focus:outline-none group"
          aria-label="Close"
        >
          <FaTimes className="group-hover:scale-110 transition-transform" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <FaCog className="text-blue-500 text-2xl" />
          <h2 className="text-2xl font-bold text-white">Process Access Request</h2>
        </div>
        
        <div className="mb-6 space-y-4">
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
            <div className="flex items-center gap-2 mb-2">
              <FaUser className="text-blue-400" />
              <span className="text-sm font-medium text-gray-300">Student</span>
            </div>
            <p className="text-white">{request.studentId?.name}</p>
          </div>

          <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
            <div className="flex items-center gap-2 mb-2">
              <FaFileAlt className="text-blue-400" />
              <span className="text-sm font-medium text-gray-300">Research</span>
            </div>
            <p className="text-white">{request.researchId?.title}</p>
          </div>

          <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
            <div className="flex items-center gap-2 mb-2">
              <FaComment className="text-blue-400" />
              <span className="text-sm font-medium text-gray-300">Reason</span>
            </div>
            <p className="text-white">{request.reason}</p>
          </div>

          <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
            <div className="flex items-center gap-2 mb-2">
              <FaCalendarAlt className="text-blue-400" />
              <span className="text-sm font-medium text-gray-300">Requested (timestamp)</span>
            </div>
            <p className="text-white font-mono text-sm">
              {formatRequestTimestamp(request.dateRequested)}
            </p>
          </div>

          {request.dateProcessed && (
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
              <div className="flex items-center gap-2 mb-2">
                <FaCalendarAlt className="text-green-400" />
                <span className="text-sm font-medium text-gray-300">Processed (timestamp)</span>
              </div>
              <p className="text-white font-mono text-sm">
                {formatRequestTimestamp(request.dateProcessed)}
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              Decision *
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Approve or decline the access request
                </div>
              </div>
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
            >
              <option value="approved">Approve</option>
              <option value="declined">Decline</option>
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaComment className="text-blue-400" />
              Comments
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Optional comments for the student
                </div>
              </div>
            </label>
            <textarea
              value={adminComments}
              onChange={(e) => setAdminComments(e.target.value)}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all h-20 resize-none"
              placeholder="Optional comments for the student..."
            />
          </div>

          {error && (
            <div className="text-red-400 text-sm p-3 bg-red-900 bg-opacity-50 rounded-lg border border-red-500">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                status === "approved" 
                  ? "bg-green-600 hover:bg-green-700" 
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  {status === "approved" ? <FaThumbsUp /> : <FaThumbsDown />}
                  Mark as {status}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 flex items-center justify-center gap-2"
            >
              <FaTimes />
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 